import Head from "next/head";
import { routerStringArg } from "~/utils/router";
import { GetServerSideProps } from "next";
import { getLogger } from "~/utils/logging";
import { GQL_GET_RECIPE, GQL_LIST_RECIPE_REMIXES, GQL_MUTATION_DOWNVOTE_RECIPE, GQL_MUTATION_UNVOTE_RECIPE, GQL_MUTATION_UPVOTE_RECIPE } from "~/graphql/recipes";
import { useCallback, useEffect, useMemo, useState } from "react";
import useAuthN from "~/hooks/use-authn";
import { AuthenticationStatus } from "~/contexts/authn";
import { makeClientSsr } from "~/lib/apollo-wrapper/make-client";
import { getSsrUserID, isAuthenticatedSsr } from "~/lib/apollo-wrapper/tokens";
import styled from "styled-components";
import { mediaQueriesRanged } from "~/styles/breakpoints";
import RecipeHelperBar from '~/components/recipe-helper-bar';
import YouTube, { YouTubeProps } from 'react-youtube';
import { Tooltip } from 'react-tooltip';
import RecipeImage from '~/components/recipe-image';
import RecipeListCard from "~/components/recipe-list-card";
import Carousel from "~/components/recipe-carousel";
import { useLazyQuery, useMutation } from "@apollo/client";
import { handleApolloError } from "~/utils/gql";
import Voting from "~/components/recipe-voting-input";
import Link from "next/link";

const logger = getLogger("RecipePage");

interface ServerProps {
  recipe: any; // todo: type
}

export const getServerSideProps: GetServerSideProps<ServerProps> = async (
  context
) => {
  // read slug from url
  const recipeID: string = routerStringArg(context.query.slug);

  // create client
  const client = makeClientSsr(context);
  const isAuthenticated: boolean = isAuthenticatedSsr(context);
  const userID: string = getSsrUserID(context);

  // todo handle errors
  try {
    const { data } = await client
      .query({
        query: GQL_GET_RECIPE,
        variables: {
          id: recipeID,
          isAuthenticated: isAuthenticated,
          userID: userID,
        },
      })
      .then(handleApolloError)
      .then(
        (res) => res,
        (err) => {
          logger.error(`failed`, err);
          return Promise.reject(err);
        }
      );

    // redirect to the slug
    const slug: string = data.recipe.urlSlug;
    if (slug !== recipeID) {
      logger.debug(`redirecting to slug page`);
      return {
        redirect: { destination: `/recipes/${slug}`, permanent: false },
      };
    }

    const serverProps: ServerProps = {
      recipe: data.recipe,
    };

    return { props: serverProps };
  } catch (e) {
    logger.error(`caught error`, e);
    return { redirect: { destination: "/recipes", permanent: false } };
  }
};

export default function RecipesPage(props: any) {
  const authn = useAuthN();

  const [recipe, setRecipe] = useState(props.recipe)
  // const recipe = useMemo(() => (props.recipe), [props.recipe]);

  useEffect(() => {
    setRecipe(props.recipe)
  }, [props])

  const recipeID: string = recipe.recipeId;

  const recipeImageUrl: string = recipe.defaultImageUrl || recipe.remixedFrom?.defaultImageUrl

  // setting page metadata
  const pageTitle: string = recipe.title || recipeID;
  const pageDesc: string = recipe.description || "";
  const metaImage = recipeImageUrl; // todo: optimize to be smaller!

  const [getRelatedRecipes, relatedRecipesQuery] = useLazyQuery(GQL_LIST_RECIPE_REMIXES);

  useEffect(() => {
    getRelatedRecipes({
      variables: {
        pageSize: 10,
        recipeIds: [recipeID],
        isAuthenticated: authn.status === AuthenticationStatus.LOGGED_IN,
        userID: authn.userID,
      }
    })
  }, [authn.status, authn.userID, getRelatedRecipes, recipeID])

  const [upvote] = useMutation(GQL_MUTATION_UPVOTE_RECIPE, {variables: {id: recipeID}});
  const [downvote] = useMutation(GQL_MUTATION_DOWNVOTE_RECIPE, {variables: {id: recipeID}});
  const [unvote] = useMutation(GQL_MUTATION_UNVOTE_RECIPE, {variables: {id: recipeID}});

  const handleUpvote = useCallback(() => {
    if (authn.status === AuthenticationStatus.LOGGED_IN) {
      upvote()
    } else {
      logger.warn(`cannot vote while not logged in`)
      // TODO: redirect to login here
    }
  }, [authn.status, upvote])

  const handleDownvote = useCallback(() => {
    if (authn.status === AuthenticationStatus.LOGGED_IN) {
      downvote()
    } else {
      logger.warn(`cannot vote while not logged in`)
      // TODO: redirect to login here
    }
  }, [authn.status, downvote])

  const handleUnvote = useCallback(() => {
    if (authn.status === AuthenticationStatus.LOGGED_IN) {
      unvote()
    } else {
      logger.warn(`cannot unvote while not logged in`)
      // TODO: redirect to login here
    }
  },[authn.status, unvote])

  const relatedRecipes = useMemo(() => {
    if(relatedRecipesQuery.error) {
      logger.error(`failed to get related recipes`)
      return null
    }

    if (!relatedRecipesQuery.data) {
      logger.debug(`no related recipes`)
      return null
    }

    const relatedRecipesData = relatedRecipesQuery.data.recipes.recipes

    logger.debug(`relatedRecipesData.length`, relatedRecipesData.length)

    const relatedRecipes = relatedRecipesData.map((r: any, ix: number) => (
      <RecipeListCard
        key={`${r.recipeId}-${ix.toString()}`}
        id={r.recipeId}
        slug={r.urlSlug}
        title={r.title}
        imgSrc={r.defaultImageUrl || r.remixedFrom?.defaultImageUrl}
        isDefaultImage={r.defaultImageUrl ? false : true}
        createdBy={r.createdBy}
        voteScore={r.voteScore}
      />
    ))

    if (relatedRecipes.length == 0) {
      return null
    }

    return (
      <RelatedRecipes>
        <SectionHeader>Remixes:</SectionHeader>
        <Carousel>
          {relatedRecipes}
        </Carousel>
      </RelatedRecipes>
    )
  }, [relatedRecipesQuery])

  const bodyContent = useMemo(() => {

    const recipeCreatedDate: string = new Date(recipe.createdAt).toLocaleDateString() || "";
    const recipePreparationTime: string = recipe.preparationTime || "";
    const recipeCookingTime: string = recipe.cookingTime || "";
    const recipeServings: string = `${recipe.servings} servings` || "";
    const recipeTitle: string = recipe.title || recipeID;
    const recipeDesc: string = recipe.description || "";
    const isDefaultImage = recipe.defaultImageUrl ? false : true;

    const recipeImageAlt: string = `depicts a cooked recipe for ${recipeTitle.toLowerCase()}`
    const remixedFromUrlSlug: string = recipe.remixedFrom?.urlSlug || "";
    const remixedFromTitle: string = recipe.remixedFrom?.title || "";
    const remixRequest: string = recipe.remixRequest || "";

    // setting recipe data
    const recipeCreatedByUsername = recipe.createdBy.username
    ? <Link href={`/u/${recipe.createdBy.username}`}>@{recipe.createdBy.username}</Link>
    : <>{""}</>;

    const youtubeOpts: YouTubeProps['opts'] = {
      width: '100%',
      height: '100%',
    };

    const ingredientCards = recipe.ingredients?.map((i: string) => (
      <IngredientRow key={i.toString()}>
        {i}
      </IngredientRow>
    ));

    const instructionCards = recipe.instructions?.map((i: string, ix: number) => (
      <InstructionRow key={i.toString()}>
        <strong>Step {ix + 1}</strong><br />
        {i}
      </InstructionRow>
    ));

    const mediaContent = recipe.video?.platform === 'YOUTUBE' ? (
      <div className="media-wrapper">
        <YouTube videoId={recipe.video.videoId} opts={youtubeOpts} className="media-content" />
      </div>
    ) : (
      <RecipeImage
        id={recipeID}
        src={recipeImageUrl}
        alt={recipeImageAlt}
        isDefaultImage={isDefaultImage}
      />
    );

    const remixedFrom = remixedFromUrlSlug && (
      <div className="remixed-from">
        remixed from {" "}
        <Link
          href={`/recipes/${remixedFromUrlSlug}`}
          data-tooltip-id="remix-tooltip"
          data-tooltip-content={remixRequest}
          data-tooltip-place="bottom"
          data-tooltip-variant="dark"
          data-tooltip-float
        >
          {remixedFromTitle}
        </Link>
        <Tooltip id="remix-tooltip" />
      </div>
    );

    return (
      <>
        <TitleDiv>
          <h1 className="title-header">
            {recipeTitle}
          </h1>
          <div className="author">
            by <span className="handle">{recipeCreatedByUsername}</span>
          </div>
          {remixedFrom}
          <div className="voting">
            <Voting
              score={recipe.voteScore || 0}
              uservote={recipe.userVote}
              upvote={handleUpvote}
              downvote={handleDownvote}
              unvote={handleUnvote}
            />
          </div>
        </TitleDiv>
        <MediaDiv>
          {mediaContent}
        </MediaDiv>
        <CookingTime>
          <div>
            {recipeCreatedDate && <><strong>Created:</strong> {recipeCreatedDate}<br /></>}
            {recipePreparationTime && <><strong>Prep Time:</strong> {recipePreparationTime}<br /></>}
            {recipeCookingTime && <><strong>Cook Time:</strong> {recipeCookingTime}<br /></>}
          </div>
        </CookingTime>
        <RecipeDescr>
          <div>{recipeDesc}</div>
        </RecipeDescr>
        <Ingredients>
          <SectionHeader>Ingredients:</SectionHeader>
          {recipeServings && <div><strong>Yield:</strong> {recipeServings}</div>}
          {ingredientCards}
        </Ingredients>
        <Instructions>
          <SectionHeader>Instructions:</SectionHeader>
          {instructionCards}
        </Instructions>
        {relatedRecipes}
      </>
    );
  }, [recipe, recipeID, recipeImageUrl, handleUpvote, handleDownvote, handleUnvote, relatedRecipes]);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta
          property="og:url"
          content={`https://reheat.ai/recipes/${recipe.urlSlug}`}
        />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={metaImage} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      {bodyContent}
      <PaddingRow />
      <RemixBar>
        <RecipeHelperBar recipeid={recipeID} />
      </RemixBar>
    </>
  );
}

const PaddingRow = styled.div`
  grid-column: page-start/page-end;
  padding-bottom: 6rem;
`

const IngredientRow = styled.div`
  padding-top: 1rem;
`

const InstructionRow = styled.div`
  padding-bottom: 1rem;
`

const BoxLeft = styled.div`
  grid-column: col-start 1/col-end 3;

  @media ${mediaQueriesRanged.sm} {
    grid-column: col-start 1/col-end 2;
  }

  @media ${mediaQueriesRanged.md} {
    grid-column: col-start 1/col-end 4;
  }

  @media ${mediaQueriesRanged.lg} {
    grid-column-end: col-end 3;
  }
`

const BoxRight = styled.div`
  grid-column: col-start 4/col-end 8;

  @media ${mediaQueriesRanged.xs} {
    grid-column: col-start 1/col-end 2;
  }

  @media ${mediaQueriesRanged.sm} {
    grid-column: col-start 1/col-end 2;
  }

  @media ${mediaQueriesRanged.md} {
    grid-column: col-start 1/col-end 4;
  }

  @media ${mediaQueriesRanged.lg} {
    grid-column: col-start 4/col-end 8;
  }
`

const TitleDiv = styled(BoxLeft)`
  display:          flex;
  justify-content:  center;
  flex-direction:   column;
  margin-bottom:    10px;
  gap:              25px;

  @media ${mediaQueriesRanged.xs} {
    gap:              10px;
    margin-bottom:    15px;
  }
  @media ${mediaQueriesRanged.sm} {
    gap:              10px;
    margin-bottom:    15px;
  }
  @media ${mediaQueriesRanged.md} {
    gap:              10px;
    margin-bottom:    15px;
  }

  .title-header {
    font-family:    font-family: Georgia, serif;/
    font-size:      32px;
    margin:         0px;
  }

  .author {
    @media ${mediaQueriesRanged.xs} {
      margin-bottom: 5px;
    }
    @media ${mediaQueriesRanged.sm} {
      margin-bottom: 5px;
    }
    @media ${mediaQueriesRanged.md} {
      margin-bottom: 5px;
    }

    a {
      color: inherit; /* blue colors for links too */
      text-decoration: inherit; /* no underline */
    }
  }

  .handle {
    font-weight: 700;
    text-decoration: underline;
  }

  .remixed-from {
    font-weight: 500;
    font-style: italic;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media ${mediaQueriesRanged.xs} {
      margin-bottom: 5px;
    }
    @media ${mediaQueriesRanged.sm} {
      margin-bottom: 5px;
    }
    @media ${mediaQueriesRanged.md} {
      margin-bottom: 5px;
    }
  }

  .remixed-from a {
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
  }

  .voting {
  }
`

const MediaDiv = styled(BoxRight)`
  width: 100%;
  max-height: 650px;
  position: relative;
  margin-bottom: 40px;

  @media(${mediaQueriesRanged.xs}) {
    grid-column: page-start/page-end;
  }

  @media(${mediaQueriesRanged.sm}) {
    grid-column: page-start/page-end;
  }

  .media-wrapper {
    padding-top: 66%; /* Set the desired aspect ratio (e.g., 4:3 = 75%) */
    position: relative;
  }

  .media-content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const CookingTime = styled(BoxLeft)`
  display: flex;
  align-items: left;
  justify-content: flex-start;
  flex-direction: column;
  margin-bottom: 10px;
`

const RecipeDescr = styled(BoxRight)`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 10px;
`

const SectionHeader = styled.h2`
  border-top: 5px solid #222;
  font-weight: 700;
  padding-top: 4px;
`

const Ingredients = styled(BoxLeft)`
  display: flex;
  align-items: left;
  justify-content: flex-start;
  flex-direction: column;
  vertical-align: top;
  text-align: left;
  height: 100%;
  margin-top: 10px;
`

const Instructions = styled(BoxRight)`
  display: flex;
  align-items: left;
  justify-content: flex-start;
  flex-direction: column;
  vertical-align: top;
  text-align: left;
  height: 100%;
  margin-top: 10px;
`

const RelatedRecipes = styled.div`
  grid-column: col-start 0/col-end 0;

  @media ${mediaQueriesRanged.sm} {
    grid-column: col-start 1/col-end 2;
  }

  @media ${mediaQueriesRanged.md} {
    grid-column: col-start 1/col-end 4;
  }

  @media ${mediaQueriesRanged.lg} {
    grid-column: col-start 1/col-end 8;
  }

  @media ${mediaQueriesRanged.xl} {
    grid-column: col-start 1/col-end 8;
  }
`

const RemixBar = styled.div`
  grid-column: page-start/page-end;
`
